// Pure helpers behind the team Overview tab: which session is next, which was
// last, and how many were recorded each week. Sessions are the rows returned
// by GET /teams/<id>/sessions/ ({ id, name, type, date, status, start_time,
// total_impacts, peak_g, assignment_count, created_at }).

const pad = (n) => String(n).padStart(2, "0");

// Local calendar day as "YYYY-MM-DD", so it compares with the API's `date`.
export const localDay = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// The day a session happens on: its start time, else its date, else when it
// was uploaded.
export const sessionDay = (s) => {
  if (s.start_time) return localDay(new Date(s.start_time));
  if (s.date) return s.date;
  if (s.created_at) return localDay(new Date(s.created_at));
  return null;
};

// A sortable timestamp for a session ("YYYY-MM-DD" dates are taken as local
// midnight, not UTC).
export const sessionTime = (s) => {
  if (s.start_time) return new Date(s.start_time).getTime();
  if (s.date) {
    const [y, m, d] = s.date.split("-").map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  return s.created_at ? new Date(s.created_at).getTime() : 0;
};

// Upcoming: not completed, has no recorded impacts yet, and is dated today or
// later. Sessions with no date or start time are never treated as upcoming,
// because we can't tell when they happen.
export const isUpcoming = (s, now = new Date()) => {
  if (s.status === "completed") return false;
  if (s.total_impacts > 0) return false;
  if (!s.start_time && !s.date) return false;
  return sessionDay(s) >= localDay(now);
};

/**
 * Split a page of sessions into { next, recorded }: `next` is the soonest
 * upcoming session (or null), `recorded` is everything else, newest first.
 */
export const summariseSessions = (sessions = [], now = new Date()) => {
  const upcoming = [];
  const recorded = [];
  sessions.forEach((s) => (isUpcoming(s, now) ? upcoming : recorded).push(s));
  upcoming.sort((a, b) => sessionTime(a) - sessionTime(b));
  recorded.sort((a, b) => sessionTime(b) - sessionTime(a));
  return { next: upcoming[0] || null, recorded };
};

// Monday 00:00 of the week `d` falls in.
const weekStart = (d) => {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
};

/**
 * Recorded sessions per week for the last `weeks` weeks (oldest first), split
 * by type: [{ weekStart: Date, training, match, other, total }].
 */
export const sessionsPerWeek = (sessions = [], weeks = 8, now = new Date()) => {
  const current = weekStart(now);
  const buckets = Array.from({ length: weeks }, (_, i) => {
    const start = new Date(current);
    start.setDate(start.getDate() - (weeks - 1 - i) * 7);
    return { weekStart: start, training: 0, match: 0, other: 0, total: 0 };
  });
  const first = buckets[0].weekStart.getTime();
  const weekMs = 7 * 24 * 60 * 60 * 1000;

  sessions.forEach((s) => {
    const time = sessionTime(s);
    if (!time || time < first) return;
    // Rounded, so a daylight-saving hour doesn't push a week into the next.
    const idx = Math.round(
      (weekStart(new Date(time)).getTime() - first) / weekMs
    );
    const bucket = buckets[idx];
    if (!bucket) return;
    const key = s.type === "training" || s.type === "match" ? s.type : "other";
    bucket[key] += 1;
    bucket.total += 1;
  });
  return buckets;
};
